import { toast } from 'react-toastify'
import { Theme } from '@mui/material'
import {
  IReportComponentInstance,
  ITextComponentInstance,
  IFunctionComponentInstance,
  ITopBarComponentInstance,
  IInstructionComponentInstance,
  IInstructionLookUpEntry,
  ReportComponentType,
  TextType,
  TableSortDirection,
  DEFAULT_BODY_STYLES,
  DEFAULT_TOP_BAR_TITLE_STYLES,
  DEFAULT_TOP_BAR_SUBTITLE_STYLES,
  DEFAULT_FUNCTION_STYLES,
  DEFAULT_TABLE_STYLES,
} from 'src/types/report-designer'
import { generateUUID } from 'src/utils/function'
import { IReportTemplate } from 'src/generated-types/report-template'
import { IFormSchemaColumn } from 'src/generated-types/form-schema'
import { ReportTemplateStatus, ReportTemplateLocation, FormFieldType } from 'src/enums'
import { invalidateReportTemplate } from 'src/api/report-template'

export type { IInstructionLookUpEntry }

const INSTRUCTION_FIELD_TYPES = [FormFieldType.instructionImage, FormFieldType.richText, FormFieldType.instructionFile]

export const buildInitialInstructionLookUp = (
  canvasItems: IReportComponentInstance[],
  formSchemaColumns: IFormSchemaColumn[],
  existingLookUp?: Record<string, IInstructionLookUpEntry>,
): Record<string, IInstructionLookUpEntry> => {
  const result: Record<string, IInstructionLookUpEntry> = {}
  for (const item of canvasItems) {
    if (item.type !== ReportComponentType.Instruction) continue
    const instructionInstance = item as IInstructionComponentInstance
    for (const col of instructionInstance.columns) {
      if (!col.formSchemaFieldId || col.formSchemaFieldId in result) continue
      const schemaCol = formSchemaColumns.find(f => f.identifier === col.formSchemaFieldId)
      if (schemaCol && INSTRUCTION_FIELD_TYPES.includes(schemaCol.type)) {
        result[col.formSchemaFieldId] = {
          identifier: col.formSchemaFieldId,
          content: schemaCol.content ?? '',
          type: schemaCol.type,
        }
      } else if (existingLookUp?.[col.formSchemaFieldId]) {
        result[col.formSchemaFieldId] = existingLookUp[col.formSchemaFieldId]
      }
    }
  }
  return result
}

export interface BuildReportTemplateParams {
  isEditMode: boolean
  initialData?: IReportTemplate
  currentTemplateName: string
  canvasItems: IReportComponentInstance[]
  customerId: number
  currentLocations: number[]
  formSchemaDataId?: number
  instructionLookUp: Record<string, IInstructionLookUpEntry>
}

export const buildReportTemplate = (
  params: BuildReportTemplateParams,
  status: ReportTemplateStatus,
): IReportTemplate => {
  const {
    isEditMode,
    initialData,
    currentTemplateName,
    canvasItems,
    customerId,
    currentLocations,
    formSchemaDataId,
    instructionLookUp,
  } = params

  return {
    ...(isEditMode && initialData?.id != null ? { id: initialData.id } : {}),
    name: currentTemplateName,
    layout: JSON.stringify({ components: canvasItems, instructionLookUp }),
    customerId,
    formSchemaVersionId: isEditMode ? initialData?.formSchemaVersionId ?? 0 : formSchemaDataId ?? 0,
    status,
    locations: currentLocations as ReportTemplateLocation[],
  }
}

export interface CreateInstanceParams {
  translate: (key: string, options?: Record<string, unknown>) => string
  theme: Theme
}

export const createInstance = (type: ReportComponentType, params: CreateInstanceParams): IReportComponentInstance => {
  const { translate, theme } = params
  const id = generateUUID()

  switch (type) {
    case ReportComponentType.TopBar:
      return {
        id,
        type: ReportComponentType.TopBar,
        label: translate('top-bar', { ns: 'dynamic-report' }),
        title: translate('report-title', { ns: 'dynamic-report' }),
        subtitle: translate('report-subtitle', { ns: 'dynamic-report' }),
        backgroundColor: theme.palette.primaryDark.main,
        titleStyles: { ...DEFAULT_TOP_BAR_TITLE_STYLES(theme) },
        subtitleStyles: { ...DEFAULT_TOP_BAR_SUBTITLE_STYLES(theme) },
      } as ITopBarComponentInstance
    case ReportComponentType.DataTable:
      return {
        id,
        type: ReportComponentType.DataTable,
        label: translate('data-table', { ns: 'dynamic-report' }),
        columns: [],
        tableStyles: { ...DEFAULT_TABLE_STYLES(theme) },
        sortColumnId: null,
        sortDirection: TableSortDirection.None,
      } as IReportComponentInstance
    case ReportComponentType.Text:
      return {
        id,
        type: ReportComponentType.Text,
        label: translate('text', { ns: 'dynamic-report' }),
        content: '',
        textType: TextType.Body,
        styles: { ...DEFAULT_BODY_STYLES(theme) },
      } as ITextComponentInstance
    case ReportComponentType.Function:
      return {
        id,
        type: ReportComponentType.Function,
        label: translate('function', { ns: 'dynamic-report' }),
        functionType: null,
        selectedColumnId: null,
        selectedColumnName: null,
        styles: { ...DEFAULT_FUNCTION_STYLES(theme) },
      } as IFunctionComponentInstance
    case ReportComponentType.Instruction:
      return {
        id,
        type: ReportComponentType.Instruction,
        label: translate('instruction', { ns: 'dynamic-report' }),
        columns: [],
      } as IInstructionComponentInstance
  }
}

export const handleCanvasDropLogic = (
  componentType: ReportComponentType,
  canvasItems: IReportComponentInstance[],
  newInstance: IReportComponentInstance,
): IReportComponentInstance[] => {
  // Only one TopBar allowed per report
  if (componentType === ReportComponentType.TopBar) {
    const alreadyHasTopBar = canvasItems.some(item => item.type === ReportComponentType.TopBar)
    if (alreadyHasTopBar) return canvasItems
  }

  // TopBar always goes at position 0 (page top)
  if (newInstance.type === ReportComponentType.TopBar) {
    return [newInstance, ...canvasItems]
  } else {
    return [...canvasItems, newInstance]
  }
}

export const handleCanvasDropAtIndexLogic = (
  componentType: ReportComponentType,
  canvasItems: IReportComponentInstance[],
  newInstance: IReportComponentInstance,
  insertIndex: number,
): IReportComponentInstance[] => {
  // Only one TopBar allowed per report
  if (componentType === ReportComponentType.TopBar) {
    const alreadyHasTopBar = canvasItems.some(item => item.type === ReportComponentType.TopBar)
    if (alreadyHasTopBar) return canvasItems
  }

  // TopBar always goes at position 0 regardless of drop position
  if (newInstance.type === ReportComponentType.TopBar) {
    return [newInstance, ...canvasItems]
  }

  const updated = [...canvasItems]
  const clampedIndex = Math.max(0, Math.min(insertIndex, updated.length))
  updated.splice(clampedIndex, 0, newInstance)
  return updated
}

export const handleReorderLogic = (
  canvasItems: IReportComponentInstance[],
  fromIndex: number,
  toIndex: number,
): IReportComponentInstance[] => {
  // Prevent any item from being placed above a TopBar pinned at index 0
  if (canvasItems[0]?.type === ReportComponentType.TopBar && toIndex === 0) return canvasItems

  const updated = [...canvasItems]
  const [removed] = updated.splice(fromIndex, 1)
  updated.splice(toIndex, 0, removed)
  return updated
}

export const handleDeleteItemLogic = (
  canvasItems: IReportComponentInstance[],
  id: string,
): IReportComponentInstance[] => {
  return canvasItems.filter(item => item.id !== id)
}

export const handleUpdateItemLogic = (
  canvasItems: IReportComponentInstance[],
  updated: IReportComponentInstance,
): IReportComponentInstance[] => {
  return canvasItems.map(item => (item.id === updated.id ? updated : item))
}

export interface HandleSaveReportParams {
  template: IReportTemplate
  isEditMode: boolean
  customerId: number
  mutateAsync: (
    template: IReportTemplate,
    config: {
      onSuccess?: () => void
      onError?: () => void
    },
  ) => Promise<unknown>
  translate: (key: string, options?: Record<string, unknown>) => string
  closeAllDialogs: () => void
}

export const handleSaveReportAction = async (params: HandleSaveReportParams): Promise<void> => {
  const { template, isEditMode, customerId, mutateAsync, translate, closeAllDialogs } = params

  await mutateAsync(template, {
    onSuccess: () => {
      invalidateReportTemplate.getFormSchemaReportTemplates(customerId)
      invalidateReportTemplate.getFormSchemaReportTemplateCounts(customerId)
      invalidateReportTemplate.getReportTemplatesByFormSchemaVersion(customerId, template.formSchemaVersionId)
      invalidateReportTemplate.getReportTemplateById(customerId, template.id ?? 0)
      invalidateReportTemplate.getReportTemplatesByFormSchemaVersion(
        customerId as EntityId,
        template.formSchemaVersionId,
      )
      toast.success(
        translate(isEditMode ? 'template-updated-successfully' : 'template-saved-successfully', {
          ns: 'dynamic-report',
        }),
      )
      closeAllDialogs()
    },
    onError: () => {
      toast.error(translate('unExpectedError', { ns: 'common-api-message' }))
    },
  })
}

export interface HandleSaveDraftParams {
  template: IReportTemplate
  isEditMode: boolean
  customerId: number
  mutateAsync: (
    template: IReportTemplate,
    config: {
      onSuccess?: () => void
      onError?: () => void
    },
  ) => Promise<unknown>
  translate: (key: string, options?: Record<string, unknown>) => string
  closeAllDialogs: () => void
}

export const handleSaveDraftAction = async (params: HandleSaveDraftParams): Promise<void> => {
  const { template, isEditMode, customerId, mutateAsync, translate, closeAllDialogs } = params

  await mutateAsync(template, {
    onSuccess: () => {
      invalidateReportTemplate.getFormSchemaReportTemplates(customerId)
      invalidateReportTemplate.getFormSchemaReportTemplateCounts(customerId)
      invalidateReportTemplate.getReportTemplateById(customerId, template.id ?? 0)
      invalidateReportTemplate.getReportTemplatesByFormSchemaVersion(customerId, template.formSchemaVersionId)
      invalidateReportTemplate.getReportTemplateById(customerId, template.id ?? 0)
      invalidateReportTemplate.getReportTemplatesByFormSchemaVersion(
        customerId as EntityId,
        template.formSchemaVersionId,
      )
      toast.success(
        translate(isEditMode ? 'draft-updated-successfully' : 'draft-saved-successfully', { ns: 'dynamic-report' }),
      )
      closeAllDialogs()
    },
    onError: () => {
      toast.error(translate('unExpectedError', { ns: 'common-api-message' }))
    },
  })
}

export interface HandleStatusChangeParams {
  customerId: number
  initialData?: IReportTemplate
  newStatus: ReportTemplateStatus
  mutateAsync: (
    payload: { status: ReportTemplateStatus; formSchemaId: EntityId },
    config: {
      onSuccess?: () => void
      onError?: () => void
    },
  ) => Promise<unknown>
  translate: (key: string, options?: Record<string, unknown>) => string
  formSchemaId: EntityId
}

export const handleStatusChangeAction = async (params: HandleStatusChangeParams): Promise<void> => {
  const { customerId, initialData, newStatus, mutateAsync, translate, formSchemaId } = params

  await mutateAsync(
    { status: newStatus, formSchemaId: formSchemaId },
    {
      onSuccess: () => {
        invalidateReportTemplate.getFormSchemaReportTemplateCounts(customerId)
        invalidateReportTemplate.getFormSchemaReportTemplates(customerId)
        invalidateReportTemplate.getReportTemplateById(customerId, initialData?.id ?? 0)
        invalidateReportTemplate.getReportTemplatesByFormSchemaVersion(
          customerId,
          initialData?.formSchemaVersionId ?? 0,
        )
        invalidateReportTemplate.getReportTemplatesByFormSchemaVersion(
          customerId as EntityId,
          initialData?.formSchemaVersionId ?? 0,
        )
        toast.success(translate('status-update-success', { ns: 'dynamic-report' }))
      },
      onError: () => {
        toast.error(translate('status-update-error', { ns: 'dynamic-report' }))
      },
    },
  )
}

export interface HandleUpgradeParams {
  customerId: number
  initialData?: IReportTemplate
  mutateAsync: (config?: { onSuccess?: () => void; onError?: () => void }) => Promise<unknown>
  translate: (key: string, options?: Record<string, unknown>) => string
  closeAllDialogs: () => void
}

export const handleUpgradeAction = async (params: HandleUpgradeParams): Promise<void> => {
  const { customerId, initialData, mutateAsync, translate, closeAllDialogs } = params

  await mutateAsync({
    onSuccess: () => {
      invalidateReportTemplate.getFormSchemaReportTemplateCounts(customerId)
      invalidateReportTemplate.getFormSchemaReportTemplates(customerId)
      invalidateReportTemplate.getReportTemplateById(customerId, initialData?.id ?? 0)
      invalidateReportTemplate.getReportTemplateWarnings(customerId, initialData?.id ?? 0)
      invalidateReportTemplate.getReportTemplatesByFormSchemaVersion(
        customerId as EntityId,
        initialData?.formSchemaVersionId ?? 0,
      )
      toast.success(translate('upgrade-success', { ns: 'dynamic-report' }))
      closeAllDialogs()
    },
    onError: () => {
      toast.error(translate('upgrade-error', { ns: 'dynamic-report' }))
    },
  })
}
